import { useId, useState, type ReactNode } from 'react';

interface InvestigationKnowledgeSectionProps {
  eyebrow: string;
  title: string;
  count: number;
  summary: string;
  definition?: string;
  emptyText: string;
  actionLabel?: string;
  onAction?: () => void;
  children?: ReactNode;
  defaultExpanded?: boolean;
}

export function InvestigationKnowledgeSection({
  eyebrow,
  title,
  count,
  summary,
  definition,
  emptyText,
  actionLabel,
  onAction,
  children,
  defaultExpanded = false,
}: InvestigationKnowledgeSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentId = useId();
  const headingId = useId();

  return (
    <section className={`r42-investigation-section r42-investigation-knowledge${expanded ? ' r42-investigation-knowledge--open' : ''}`} aria-labelledby={headingId}>
      <div className="r42-investigation-knowledge-head">
        <div className="r42-investigation-knowledge-copy">
          <div className="r42-investigation-knowledge-label">
            <span className="r42-eyebrow">{eyebrow}</span>
            <span className="r42-investigation-knowledge-count" aria-label={`${count} ${count === 1 ? 'item' : 'items'} in ${title.toLocaleLowerCase()}`}>{count}</span>
          </div>
          <h2 id={headingId}>{title}</h2>
          {definition ? <p className="r42-investigation-knowledge-definition">{definition}</p> : null}
          <p>{summary}</p>
        </div>
        <div className="r42-investigation-knowledge-actions">
          {expanded && actionLabel && onAction ? <button type="button" className="r42-section-action" onClick={onAction}>{actionLabel}</button> : null}
          <button
            type="button"
            className="r42-investigation-knowledge-toggle"
            aria-expanded={expanded}
            aria-controls={contentId}
            aria-label={`${expanded ? 'Hide' : 'Open'} ${title}`}
            onClick={() => setExpanded((value) => !value)}
          >
            <span>{expanded ? 'Hide' : 'Open'}</span>
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} aria-hidden="true" />
          </button>
        </div>
      </div>
      {expanded ? <div id={contentId} className="r42-investigation-knowledge-body">{count > 0 ? children : <div className="r42-investigation-section-empty">{emptyText}</div>}</div> : null}
    </section>
  );
}
